/* Ma bibliothèque — explorateur de dossiers (spec 02, écran A/A0, redesign #451).
   "Corbeille" = archived_at existant (content.archive / content.unarchive) —
   aucune suppression définitive de document. Un dossier n'a lui aucun contenu
   propre : le supprimer est un delete réel, ses documents repassent en
   "Non classés" (folder_id nettoyé côté DB, ON DELETE SET NULL). */

import { useCallback, useEffect, useId, useState, type ComponentType } from 'react';
import { useNavigate } from '../lib/routing';
import { useLocalStorage } from '../hooks/use-local-storage';
import SendAssignmentModal from '../components/send-assignment-modal';
import { useAppHeader } from '../components/shell/app-header-store';
import { HEADER_TITLE_CLASS } from '../components/shell/app-header';
import { api } from '../lib/api';
import { Alert } from '@/components/ui/alert';
import { Button, buttonVariants } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuSeparator, DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/input';
import { NativeSelect } from '@/components/ui/select';
import { cn } from '@/lib/utils';
import {
  clipFolders, countLabel, groupFoldersByClassroom, sectionKey,
  type ClassroomFolder,
} from '../lib/classroom-folders';
import {
  Archive, ArrowCounterClockwise, BookOpen, CaretDoubleDown, CaretDoubleUp, CaretDown,
  Check, DotsThreeVertical, FileText, Folder, FolderDashed, FolderOpen, FolderPlus,
  CheckSquare, FolderSimplePlus, ListChecks, MagnifyingGlass, PaperPlaneTilt, PencilSimple,
  PencilSimpleLine, Plus, Trash, X,
} from '@phosphor-icons/react';
import type { DocKind } from '../data/teacher-dummy';

const UNFILED = 'unfiled';
const SECTIONS_COLLAPSED_KEY = 'naia.library.sections.collapsed';

interface DocRow {
  id: string;
  kind: DocKind;
  title: string;
  level_id: string;
  subject_id: string;
  folder_id: string | null;
  archived_at: string | null;
  updated_at: string;
}
type FolderRow = ClassroomFolder;
interface Ref { id: string; label: string }

const KIND_ORDER: DocKind[] = ['course', 'exercice_sheet', 'exam', 'general'];

const KIND: Record<DocKind, { label: string; icon: ComponentType<{ size?: number; weight?: 'regular' | 'bold' | 'fill' | 'light' }>; tone: string }> = {
  course: { label: 'Cours', icon: BookOpen, tone: 'text-naia-blue' },
  exercice_sheet: { label: 'Exercices', icon: PencilSimple, tone: 'text-naia-green' },
  exam: { label: 'Évaluation', icon: ListChecks, tone: 'text-[var(--naia-tertiary)]' },
  general: { label: 'Document', icon: FileText, tone: 'text-naia-muted-2' },
};

type DocumentKind = 'general' | 'course' | 'exercice_sheet' | 'exam';

const KIND_LABEL: Record<DocumentKind, string> = {
  course: 'Cours',
  exercice_sheet: 'Exercices',
  exam: 'Évaluations',
  general: 'Documents',
};

const isDocumentKind = (v: string | null): v is DocumentKind =>
  v !== null && Object.prototype.hasOwnProperty.call(KIND_LABEL, v);

type View =
  | { name: 'grid' }
  | { name: 'folder'; id: string; kind?: DocumentKind }
  | { name: 'kind'; kind: DocumentKind }
  | { name: 'trash' };

type Modal =
  | { kind: 'none' }
  | { kind: 'folder-create' }
  | { kind: 'folder-rename'; folder: FolderRow }
  | { kind: 'folder-delete'; folder: FolderRow }
  | { kind: 'doc-create'; folderId: string | null }
  | { kind: 'doc-move'; doc: DocRow }
  | { kind: 'doc-archive'; doc: DocRow }
  | { kind: 'bulk-move'; ids: string[] }
  | { kind: 'bulk-archive'; ids: string[] };

const plural = (n: number, one: string, many: string) => `${n} ${n > 1 ? many : one}`;

const fmtDate = (iso: string) =>
  new Date(iso).toLocaleDateString('fr-FR', { day: 'numeric', month: 'short' });

/* La fiche classe ouvre la bibliotheque avec ?classroom=<id> pour preselectionner
   la classe du dossier — le rattachement reste facultatif. */
function readClassroomFromUrl(): string {
  if (typeof window === 'undefined') return '';
  return new URLSearchParams(window.location.search).get('classroom') ?? '';
}

function readViewFromUrl(): View {
  if (typeof window === 'undefined') return { name: 'grid' };
  const params = new URLSearchParams(window.location.search);
  if (params.get('trash') === '1') return { name: 'trash' };
  const folder = params.get('folder');
  const kind = params.get('kind');
  if (folder) return isDocumentKind(kind) ? { name: 'folder', id: folder, kind } : { name: 'folder', id: folder };
  return isDocumentKind(kind) ? { name: 'kind', kind } : { name: 'grid' };
}

function useLibraryView(): [View, (v: View) => void] {
  const [view, setViewState] = useState<View>(readViewFromUrl);

  useEffect(() => {
    const onPop = () => setViewState(readViewFromUrl());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const setView = useCallback((next: View) => {
    const params = new URLSearchParams(window.location.search);
    params.delete('trash');
    params.delete('folder');
    params.delete('kind');
    if (next.name === 'folder') {
      params.set('folder', next.id);
      if (next.kind) params.set('kind', next.kind);
    } else if (next.name === 'kind') params.set('kind', next.kind);
    else if (next.name === 'trash') params.set('trash', '1');
    const qs = params.toString();
    history.pushState({}, '', qs ? `?${qs}` : window.location.pathname);
    setViewState(next);
  }, []);

  return [view, setView];
}

function RowMenuTrigger({ label }: { label: string }) {
  return (
    <DropdownMenuTrigger
      aria-label={label}
      className={cn(
        buttonVariants({ variant: 'ghost', size: 'icon-sm' }),
        'relative shrink-0 text-muted-foreground opacity-0 transition-opacity',
        'group-hover:opacity-100 focus-visible:opacity-100 aria-expanded:opacity-100 pointer-coarse:opacity-100',
        "after:absolute after:-inset-2 after:content-['']",
      )}
    >
      <DotsThreeVertical size={18} weight="bold" />
    </DropdownMenuTrigger>
  );
}

function EmptyState({ icon, title, hint }: { icon: React.ReactNode; title: string; hint?: string }) {
  return (
    <div className="flex flex-col items-center justify-center py-16 px-6 text-center text-muted-foreground">
      <span className="mb-3 opacity-40">{icon}</span>
      <p className="font-semibold text-[14px] text-foreground">{title}</p>
      {hint && <p className="text-[13px] mt-1">{hint}</p>}
    </div>
  );
}

export default function ContentLibrary() {
  const navigate = useNavigate();
  const [view, setView] = useLibraryView();

  const [folders, setFolders] = useState<FolderRow[]>([]);
  const [unfiledCount, setUnfiledCount] = useState(0);
  const [levels, setLevels] = useState<Ref[]>([]);
  const [subjects, setSubjects] = useState<Ref[]>([]);
  const [docs, setDocs] = useState<DocRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [foldersLoaded, setFoldersLoaded] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [sendDoc, setSendDoc] = useState<DocRow | null>(null);
  const [sentToast, setSentToast] = useState<string | null>(null);
  const [search, setSearch] = useState('');
  const [refreshTick, setRefreshTick] = useState(0);
  const [expandedGroups, setExpandedGroups] = useState<string[]>([]);
  const [storedCollapsed, setStoredCollapsed] = useLocalStorage<string[]>(SECTIONS_COLLAPSED_KEY, []);
  const collapsedGroups = Array.isArray(storedCollapsed) ? storedCollapsed : [];

  const [modal, setModal] = useState<Modal>({ kind: 'none' });
  const [modalBusy, setModalBusy] = useState(false);
  const [modalError, setModalError] = useState<string | null>(null);
  const [draftName, setDraftName] = useState('');
  const [draftKind, setDraftKind] = useState<DocKind>('general');
  const [draftLevel, setDraftLevel] = useState('');
  const [draftSubject, setDraftSubject] = useState('');
  const [draftFolder, setDraftFolder] = useState<string>(UNFILED);
  const [draftClassroom, setDraftClassroom] = useState('');
  const [classrooms, setClassrooms] = useState<Ref[]>([]);
  const [selectedIds, setSelectedIds] = useState<string[]>([]);

  /* Un ?classroom= inconnu (classe d'un autre prof, lien perime) ne doit jamais
     partir au serveur : il retombe sur « aucune ». */
  const selectedClassroom = classrooms.some((c) => c.id === draftClassroom) ? draftClassroom : '';

  const query = search.trim();

  const refreshCounts = useCallback(async () => {
    const [f, unfiled] = await Promise.all([
      api.content.folders.list.query(),
      api.content.list.query({ folderId: null, includeArchived: false, limit: 1 }),
    ]);
    setFolders(f as FolderRow[]);
    setUnfiledCount((unfiled as { total: number }).total);
  }, []);

  useEffect(() => {
    refreshCounts().then(() => setFoldersLoaded(true)).catch((e) => {
      console.error('[ContentLibrary] refreshCounts failed', e);
      setError('Erreur lors du chargement.');
    });
    Promise.all([api.content.levels.query(), api.content.subjects.query()])
      .then(([lv, su]) => { setLevels(lv as Ref[]); setSubjects(su as Ref[]); })
      .catch(() => {});
    api.teacher.myClassrooms.query()
      .then((rows) => {
        const list = rows as Array<{ id: string; name: string }>;
        setClassrooms(list.map((c) => ({ id: c.id, label: c.name })));
      })
      .catch(() => {});
  }, [refreshCounts]);

  useEffect(() => {
    let cancelled = false;
    const params = view.name === 'trash'
      ? { includeArchived: true, limit: 200 }
      : view.name === 'folder'
        ? { folderId: view.id === UNFILED ? null : view.id, kind: view.kind, includeArchived: false, limit: 200 }
        : view.name === 'kind'
        ? { kind: view.kind, includeArchived: false, limit: 200 }
        : query
          ? { search: query, includeArchived: false, limit: 200 }
          : null;

    if (!params) { setDocs([]); setLoading(false); return; }

    setLoading(true);
    setError(null);
    api.content.list.query(params)
      .then((r) => {
        if (cancelled) return;
        const items = (r as { items: DocRow[] }).items;
        setDocs(view.name === 'trash' ? items.filter((d) => d.archived_at !== null) : items);
      })
      .catch((e) => {
        if (!cancelled) { console.error('[ContentLibrary] content.list failed', view, e); setError('Erreur lors du chargement.'); }
      })
      .finally(() => { if (!cancelled) setLoading(false); });

    return () => { cancelled = true; };
  }, [view, query, refreshTick]);

  useEffect(() => { setSelectedIds([]); }, [view, query]);

  const toggleSelected = (id: string) =>
    setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const allVisibleSelected = docs.length > 0 && docs.every((d) => selectedIds.includes(d.id));
  const toggleSelectAll = () =>
    setSelectedIds(allVisibleSelected ? [] : docs.map((d) => d.id));

  const labelOf = (list: Ref[], id: string) => list.find((r) => r.id === id)?.label ?? id;

  const folderName = (id: string | null) =>
    id === null ? 'Non classés' : folders.find((f) => f.id === id)?.name ?? 'Non classés';

  const closeModal = () => { setModal({ kind: 'none' }); setModalError(null); setModalBusy(false); };

  const openFolderCreate = (classroomId?: string) => {
    setDraftName('');
    setDraftClassroom(classroomId ?? readClassroomFromUrl());
    setModalError(null);
    setModal({ kind: 'folder-create' });
  };
  const openFolderRename = (folder: FolderRow) => { setDraftName(folder.name); setModalError(null); setModal({ kind: 'folder-rename', folder }); };
  const openDocCreate = (folderId: string | null) => {
    setDraftName(''); setDraftKind('general'); setDraftLevel(''); setDraftSubject('');
    setModalError(null);
    setModal({ kind: 'doc-create', folderId });
  };
  const openDocMove = (doc: DocRow) => { setDraftFolder(doc.folder_id ?? UNFILED); setModalError(null); setModal({ kind: 'doc-move', doc }); };
  const openBulkMove = (ids: string[]) => { setDraftFolder(UNFILED); setModalError(null); setModal({ kind: 'bulk-move', ids }); };

  const commitFolderCreate = async () => {
    const name = draftName.trim();
    if (!name) return;
    setModalBusy(true); setModalError(null);
    try {
      await api.content.folders.create.mutate({ name, classroomId: selectedClassroom || null });
      await refreshCounts();
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitFolderRename = async (folder: FolderRow) => {
    const name = draftName.trim();
    if (!name) return;
    setModalBusy(true); setModalError(null);
    try {
      await api.content.folders.rename.mutate({ id: folder.id, name });
      await refreshCounts();
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitFolderDelete = async (folder: FolderRow) => {
    setModalBusy(true); setModalError(null);
    try {
      await api.content.folders.delete.mutate({ id: folder.id });
      if (view.name === 'folder' && view.id === folder.id) setView({ name: 'grid' });
      await refreshCounts();
      setRefreshTick((t) => t + 1);
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitDocCreate = async (folderId: string | null) => {
    if (!draftLevel || !draftSubject) { setModalError('Niveau et matière obligatoires.'); return; }
    setModalBusy(true); setModalError(null);
    try {
      const doc = await api.content.create.mutate({
        kind: draftKind, levelId: draftLevel, subjectId: draftSubject,
        title: draftName.trim() || undefined, folderId,
      });
      navigate(`/teacher/library/${(doc as { id: string }).id}`);
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitDocMove = async (doc: DocRow) => {
    const target = draftFolder === UNFILED ? null : draftFolder;
    setModalBusy(true); setModalError(null);
    try {
      await api.content.move.mutate({ id: doc.id, folderId: target });
      await refreshCounts();
      setRefreshTick((t) => t + 1);
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitDocArchive = async (doc: DocRow) => {
    setModalBusy(true); setModalError(null);
    try {
      await api.content.archive.mutate({ id: doc.id });
      await refreshCounts();
      setRefreshTick((t) => t + 1);
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitBulkMove = async (ids: string[]) => {
    const target = draftFolder === UNFILED ? null : draftFolder;
    setModalBusy(true); setModalError(null);
    try {
      await Promise.all(ids.map((id) => api.content.move.mutate({ id, folderId: target })));
      setSelectedIds([]);
      await refreshCounts();
      setRefreshTick((t) => t + 1);
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const commitBulkArchive = async (ids: string[]) => {
    setModalBusy(true); setModalError(null);
    try {
      await Promise.all(ids.map((id) => api.content.archive.mutate({ id })));
      setSelectedIds([]);
      await refreshCounts();
      setRefreshTick((t) => t + 1);
      closeModal();
    } catch (e) { setModalError(e instanceof Error ? e.message : String(e)); setModalBusy(false); }
  };

  const restoreDoc = async (doc: DocRow) => {
    setError(null);
    try {
      await api.content.unarchive.mutate({ id: doc.id });
      await refreshCounts();
      setRefreshTick((t) => t + 1);
    } catch (e) { setError(e instanceof Error ? e.message : String(e)); }
  };

  const groups = groupFoldersByClassroom(folders, classrooms);

  /* Une classe sans dossier garde sa section — c'est la qu'on lui en cree un.
     Le groupe sans classe, lui, ne s'affiche que s'il a quelque chose. */
  const sections = groups
    .map((group) => ({
      group,
      key: sectionKey(group.classroomId),
      unfiled: group.classroomId === null && unfiledCount > 0,
    }))
    .filter(({ group, unfiled }) => group.classroomId !== null || group.folders.length > 0 || unfiled);

  const sectionKeys = sections.map((s) => s.key);
  const allCollapsed = sections.length > 0 && sectionKeys.every((key) => collapsedGroups.includes(key));

  /* Toute ecriture reduit l'etat persiste aux sections encore rendues : une classe
     supprimee ne peut donc pas laisser de cle morte s'accumuler jusqu'au quota, ou
     l'echec d'ecriture serait avale en silence par useLocalStorage. Ne pas retirer
     ce filtre en croyant n'enlever qu'une precaution. */
  const setCollapsed = (next: (prev: string[]) => string[]) =>
    setStoredCollapsed((prev) => {
      const live = new Set(sectionKeys);
      return next(Array.isArray(prev) ? prev : []).filter((key) => live.has(key));
    });

  const totalDocs = folders.reduce((sum, f) => sum + f.document_count, 0) + unfiledCount;
  const libraryEmpty =
    foldersLoaded && folders.length === 0 && unfiledCount === 0 && classrooms.length === 0;

  const headerTitleBlock = (title: string, subtitle?: string) => (
    <>
      <h1 className={HEADER_TITLE_CLASS}>{title}</h1>
      {subtitle && <p className="m-0 mt-0.5 text-[13px] sm:text-[13.5px] leading-snug text-muted-foreground">{subtitle}</p>}
    </>
  );

  useAppHeader(
    view.name === 'grid'
      ? {
          content: headerTitleBlock(
            'Ma bibliothèque',
            foldersLoaded ? `${plural(folders.length, 'dossier', 'dossiers')} · ${plural(totalDocs, 'document', 'documents')}` : undefined,
          ),
          width: 'md',
          actions: (
            <Button size="sm" onClick={() => openFolderCreate()}>
              <FolderPlus size={15} weight="bold" />
              Nouveau dossier
            </Button>
          ),
        }
      : view.name === 'folder'
        ? {
            content: headerTitleBlock(
              view.kind
                ? `${folderName(view.id === UNFILED ? null : view.id)} · ${KIND_LABEL[view.kind]}`
                : folderName(view.id === UNFILED ? null : view.id),
              loading ? undefined : (docs.length === 0 ? 'Dossier vide' : plural(docs.length, 'document', 'documents')),
            ),
            width: 'md',
            onBack: () => setView({ name: 'grid' }),
            actions: (
              <Button size="sm" variant="outline" onClick={() => openDocCreate(view.id === UNFILED ? null : view.id)}>
                <Plus size={15} weight="bold" />
                Nouveau document
              </Button>
            ),
          }
        : view.name === 'kind'
        ? {
            content: headerTitleBlock(
              KIND_LABEL[view.kind],
              loading ? undefined : (docs.length === 0 ? 'Aucun document' : plural(docs.length, 'document', 'documents')),
            ),
            width: 'md',
            onBack: () => setView({ name: 'grid' }),
          }
        : {
            content: headerTitleBlock(
              'Corbeille',
              loading ? undefined : (docs.length === 0 ? 'Vide' : plural(docs.length, 'élément', 'éléments')),
            ),
            width: 'md',
            onBack: () => setView({ name: 'grid' }),
          },
  );

  return (
    <div className="min-h-dvh bg-background flex flex-col motion-reduce:[&_*]:transition-none">
      <main className="max-w-[880px] mx-auto px-5 pt-2 pb-16 flex-1 w-full">
        {error && <Alert className="mb-4">{error}</Alert>}
        {sentToast && <Alert className="mb-4">{sentToast}</Alert>}

        {view.name === 'grid' && (
          <>
            <div className="relative mb-5 mt-3">
              <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground pointer-events-none" />
              <Input
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Rechercher dans toute la bibliothèque…"
                aria-label="Rechercher un document"
                className="pl-9 pr-11 h-11"
              />
              {search && (
                <button
                  type="button"
                  onClick={() => setSearch('')}
                  aria-label="Effacer la recherche"
                  className="absolute right-0 top-0 grid place-items-center min-w-[44px] h-11 text-muted-foreground hover:text-foreground bg-transparent border-none cursor-pointer font-[inherit]"
                >
                  <X size={15} weight="bold" />
                </button>
              )}
            </div>

            {query ? (
              <>
                <p className="text-[12px] text-muted-foreground mb-2">
                  {loading ? 'Recherche…' : docs.length === 0
                    ? 'Aucun résultat'
                    : `${plural(docs.length, 'résultat', 'résultats')} pour « ${query} »`}
                </p>
                {!loading && docs.length === 0 ? (
                  <EmptyState
                    icon={<MagnifyingGlass size={40} weight="light" />}
                    title="Aucun document ne correspond"
                    hint="Essayez un autre mot-clé ou un nom de dossier."
                  />
                ) : (
                  <div className="flex flex-col gap-2">
                    {selectedIds.length > 0 ? (
                      <SelectionBar
                        count={selectedIds.length}
                        allSelected={allVisibleSelected}
                        onToggleSelectAll={toggleSelectAll}
                        onMove={() => openBulkMove(selectedIds)}
                        onArchive={() => setModal({ kind: 'bulk-archive', ids: selectedIds })}
                        onClear={() => setSelectedIds([])}
                      />
                    ) : (
                      <SelectAllEntry onSelectAll={toggleSelectAll} />
                    )}
                    {docs.map((doc) => (
                      <DocRowItem
                        key={doc.id}
                        doc={doc}
                        folderLabel={folderName(doc.folder_id)}
                        levelLabel={labelOf(levels, doc.level_id)}
                        subjectLabel={labelOf(subjects, doc.subject_id)}
                        onOpen={() => navigate(`/teacher/library/${doc.id}`)}
                        onSend={() => setSendDoc(doc)}
                        onMove={() => openDocMove(doc)}
                        onArchive={() => setModal({ kind: 'doc-archive', doc })}
                        selected={selectedIds.includes(doc.id)}
                        onToggleSelect={() => toggleSelected(doc.id)}
                      />
                    ))}
                  </div>
                )}
              </>
            ) : !foldersLoaded ? (
              <div className="flex flex-col gap-2">
                {[1, 2, 3].map((i) => <div key={i} className="h-[58px] rounded-card bg-muted animate-pulse" />)}
              </div>
            ) : libraryEmpty ? (
              <EmptyState
                icon={<Folder size={44} weight="light" />}
                title="Bibliothèque vide"
                hint="Créez votre premier dossier pour commencer."
              />
            ) : (
              <div className="flex flex-col gap-4">
                {sections.length > 1 && (
                  <div className="flex justify-end -mt-1 -mb-2">
                    <button
                      type="button"
                      onClick={() => setCollapsed(() => (allCollapsed ? [] : sectionKeys))}
                      className="inline-flex items-center gap-1.5 min-h-11 px-2 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] font-bold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                    >
                      {allCollapsed
                        ? <CaretDoubleDown size={13} weight="bold" />
                        : <CaretDoubleUp size={13} weight="bold" />}
                      {allCollapsed ? 'Tout déplier' : 'Tout replier'}
                    </button>
                  </div>
                )}

                {sections.map(({ group, key, unfiled }) => {
                  const empty = group.folders.length === 0 && !unfiled;
                  const expanded = expandedGroups.includes(key);
                  const { shown, overflow } = clipFolders(group.folders, expanded);

                  return (
                    <ClassSection
                      key={key}
                      label={group.label}
                      summary={countLabel(
                        group.folders.length + (unfiled ? 1 : 0),
                        group.documentCount + (unfiled ? unfiledCount : 0),
                      )}
                      open={!collapsedGroups.includes(key)}
                      onToggle={() => setCollapsed((prev) => (
                        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                      ))}
                    >
                      {empty ? (
                        <div className="grid grid-cols-1 min-[520px]:grid-cols-2 min-[800px]:grid-cols-3 gap-3">
                          <FolderCard
                            name="Créer un dossier"
                            hint="Aucun dossier pour l’instant"
                            dashed
                            icon={<FolderSimplePlus size={34} weight="regular" />}
                            onOpen={() => openFolderCreate(group.classroomId ?? undefined)}
                          />
                        </div>
                      ) : (
                        <>
                          <div className="grid grid-cols-1 min-[520px]:grid-cols-2 min-[800px]:grid-cols-3 gap-3">
                            {unfiled && (
                              <FolderCard
                                name="Non classés"
                                count={unfiledCount}
                                dashed
                                icon={<FolderDashed size={34} weight="regular" />}
                                onOpen={() => setView({ name: 'folder', id: UNFILED })}
                              />
                            )}

                            {shown.map((folder, i) => (
                              <FolderCard
                                key={folder.id}
                                name={folder.name}
                                index={i + 1}
                                count={folder.document_count}
                                icon={<Folder size={34} weight="fill" />}
                                onOpen={() => setView({ name: 'folder', id: folder.id })}
                                onRename={() => openFolderRename(folder)}
                                onDelete={() => setModal({ kind: 'folder-delete', folder })}
                              />
                            ))}
                          </div>

                          {overflow > 0 && (
                            <button
                              type="button"
                              aria-expanded={expanded}
                              onClick={() => setExpandedGroups((prev) => (
                                prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
                              ))}
                              className="mt-2 inline-flex items-center min-h-11 px-1 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] font-bold text-primary hover:underline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                            >
                              {expanded ? 'Voir moins' : `Voir les ${overflow} autres dossiers`}
                            </button>
                          )}
                        </>
                      )}
                    </ClassSection>
                  );
                })}

                <section aria-label="Corbeille">
                  <div className="flex items-center gap-3 mb-3">
                    <span className="h-px flex-1 bg-border" />
                    <span className="text-[11px] font-extrabold uppercase tracking-[0.08em] text-muted-foreground">
                      Corbeille
                    </span>
                    <span className="h-px flex-1 bg-border" />
                  </div>

                  <div className="grid grid-cols-1 min-[520px]:grid-cols-2 min-[800px]:grid-cols-3 gap-3">
                    <TrashCard onOpen={() => setView({ name: 'trash' })} />
                  </div>
                </section>
              </div>
            )}
          </>
        )}

        {view.name === 'folder' && (
          loading ? (
            <div className="flex flex-col gap-2 mt-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-[58px] rounded-card bg-muted animate-pulse" />)}
            </div>
          ) : docs.length === 0 ? (
            <EmptyState
              icon={<FolderOpen size={44} weight="light" />}
              title="Ce dossier est vide"
              hint="Créez un document ici, ou déplacez-en un depuis un autre dossier."
            />
          ) : (
            <div className="flex flex-col gap-2 mt-3">
              {selectedIds.length > 0 ? (
                <SelectionBar
                  count={selectedIds.length}
                  allSelected={allVisibleSelected}
                  onToggleSelectAll={toggleSelectAll}
                  onMove={() => openBulkMove(selectedIds)}
                  onArchive={() => setModal({ kind: 'bulk-archive', ids: selectedIds })}
                  onClear={() => setSelectedIds([])}
                />
              ) : (
                <SelectAllEntry onSelectAll={toggleSelectAll} />
              )}
              {docs.map((doc) => (
                <DocRowItem
                  key={doc.id}
                  doc={doc}
                  levelLabel={labelOf(levels, doc.level_id)}
                  subjectLabel={labelOf(subjects, doc.subject_id)}
                  onOpen={() => navigate(`/teacher/library/${doc.id}`)}
                  onSend={() => setSendDoc(doc)}
                  onMove={() => openDocMove(doc)}
                  onArchive={() => setModal({ kind: 'doc-archive', doc })}
                  selected={selectedIds.includes(doc.id)}
                  onToggleSelect={() => toggleSelected(doc.id)}
                />
              ))}
            </div>
          )
        )}

        {view.name === 'kind' && (
          loading ? (
            <div className="flex flex-col gap-2 mt-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-[58px] rounded-card bg-muted animate-pulse" />)}
            </div>
          ) : docs.length === 0 ? (
            <EmptyState
              icon={<FolderOpen size={44} weight="light" />}
              title={`Aucun document dans « ${KIND_LABEL[view.kind]} »`}
              hint="Les documents de ce type apparaîtront ici."
            />
          ) : (
            <div className="flex flex-col gap-2 mt-3">
              {selectedIds.length > 0 ? (
                <SelectionBar
                  count={selectedIds.length}
                  allSelected={allVisibleSelected}
                  onToggleSelectAll={toggleSelectAll}
                  onMove={() => openBulkMove(selectedIds)}
                  onArchive={() => setModal({ kind: 'bulk-archive', ids: selectedIds })}
                  onClear={() => setSelectedIds([])}
                />
              ) : (
                <SelectAllEntry onSelectAll={toggleSelectAll} />
              )}
              {docs.map((doc) => (
                <DocRowItem
                  key={doc.id}
                  doc={doc}
                  levelLabel={labelOf(levels, doc.level_id)}
                  subjectLabel={labelOf(subjects, doc.subject_id)}
                  onOpen={() => navigate(`/teacher/library/${doc.id}`)}
                  onSend={() => setSendDoc(doc)}
                  onMove={() => openDocMove(doc)}
                  onArchive={() => setModal({ kind: 'doc-archive', doc })}
                  selected={selectedIds.includes(doc.id)}
                  onToggleSelect={() => toggleSelected(doc.id)}
                />
              ))}
            </div>
          )
        )}

        {view.name === 'trash' && (
          loading ? (
            <div className="flex flex-col gap-2 mt-3">
              {[1, 2, 3].map((i) => <div key={i} className="h-[58px] rounded-card bg-muted animate-pulse" />)}
            </div>
          ) : docs.length === 0 ? (
            <EmptyState icon={<Trash size={44} weight="light" />} title="La corbeille est vide" hint="Les documents archivés apparaissent ici." />
          ) : (
            <div className="flex flex-col gap-2 mt-3">
              {docs.map((doc) => (
                <TrashRow
                  key={doc.id}
                  doc={doc}
                  levelLabel={labelOf(levels, doc.level_id)}
                  subjectLabel={labelOf(subjects, doc.subject_id)}
                  onRestore={() => void restoreDoc(doc)}
                />
              ))}
            </div>
          )
        )}
      </main>

      <Dialog open={modal.kind !== 'none'} onOpenChange={(open) => { if (!open) closeModal(); }}>
        <DialogContent style={{ maxWidth: 420 }}>
          {modal.kind === 'folder-create' && (
            <ModalShell
              title="Nouveau dossier"
              confirmLabel={modalBusy ? 'Création…' : 'Créer'}
              confirmDisabled={!draftName.trim() || modalBusy}
              error={modalError}
              onConfirm={() => void commitFolderCreate()}
              onCancel={closeModal}
            >
              <div className="flex flex-col gap-3">
                <Input
                  autoFocus
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  onKeyDown={(e) => e.key === 'Enter' && void commitFolderCreate()}
                  placeholder="Nom du dossier"
                  aria-label="Nom du dossier"
                  className="h-11"
                />
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">
                    Classe (facultatif)
                  </span>
                  <NativeSelect
                    value={selectedClassroom}
                    onChange={(e) => setDraftClassroom(e.target.value)}
                    className="h-11"
                  >
                    <option value="">— Aucune —</option>
                    {classrooms.map((c) => <option key={c.id} value={c.id}>{c.label}</option>)}
                  </NativeSelect>
                  <span className="text-[11px] text-muted-foreground">
                    Rattache le dossier à une classe pour le retrouver depuis sa fiche.
                  </span>
                </label>
              </div>
            </ModalShell>
          )}

          {modal.kind === 'folder-rename' && (
            <ModalShell
              title="Renommer le dossier"
              confirmLabel={modalBusy ? 'Renommage…' : 'Renommer'}
              confirmDisabled={!draftName.trim() || modalBusy}
              error={modalError}
              onConfirm={() => void commitFolderRename(modal.folder)}
              onCancel={closeModal}
            >
              <Input
                autoFocus
                value={draftName}
                onChange={(e) => setDraftName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && void commitFolderRename(modal.folder)}
                aria-label="Nouveau nom du dossier"
                className="h-11"
              />
            </ModalShell>
          )}

          {modal.kind === 'folder-delete' && (
            <ModalShell
              title="Supprimer ce dossier ?"
              confirmLabel={modalBusy ? 'Suppression…' : 'Supprimer'}
              confirmDisabled={modalBusy}
              destructive
              error={modalError}
              onConfirm={() => void commitFolderDelete(modal.folder)}
              onCancel={closeModal}
            >
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                « {modal.folder.name} » sera supprimé. Ses documents ne sont pas touchés :
                ils repassent dans « Non classés ».
              </p>
            </ModalShell>
          )}

          {modal.kind === 'doc-create' && (
            <ModalShell
              title="Nouveau document"
              confirmLabel={modalBusy ? 'Création…' : 'Créer et ouvrir'}
              confirmDisabled={modalBusy}
              error={modalError}
              onConfirm={() => void commitDocCreate(modal.folderId)}
              onCancel={closeModal}
            >
              <div className="flex flex-col gap-3">
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Type</span>
                  <NativeSelect value={draftKind} onChange={(e) => setDraftKind(e.target.value as DocKind)} className="h-11">
                    {KIND_ORDER.map((k) => <option key={k} value={k}>{KIND[k].label}</option>)}
                  </NativeSelect>
                </label>
                <div className="grid grid-cols-2 gap-3">
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Niveau *</span>
                    <NativeSelect value={draftLevel} onChange={(e) => setDraftLevel(e.target.value)} className="h-11">
                      <option value="">— Choisir —</option>
                      {levels.map((l) => <option key={l.id} value={l.id}>{l.label}</option>)}
                    </NativeSelect>
                  </label>
                  <label className="flex flex-col gap-1.5">
                    <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Matière *</span>
                    <NativeSelect value={draftSubject} onChange={(e) => setDraftSubject(e.target.value)} className="h-11">
                      <option value="">— Choisir —</option>
                      {subjects.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
                    </NativeSelect>
                  </label>
                </div>
                <label className="flex flex-col gap-1.5">
                  <span className="text-[11px] font-bold text-muted-foreground uppercase tracking-wide">Titre</span>
                  <Input
                    value={draftName}
                    onChange={(e) => setDraftName(e.target.value)}
                    placeholder="Titre du document"
                    className="h-11"
                  />
                </label>
              </div>
            </ModalShell>
          )}

          {modal.kind === 'doc-move' && (
            <ModalShell
              title="Déplacer le document"
              confirmLabel={modalBusy ? 'Déplacement…' : 'Déplacer'}
              confirmDisabled={modalBusy}
              error={modalError}
              onConfirm={() => void commitDocMove(modal.doc)}
              onCancel={closeModal}
            >
              <p className="text-[13px] text-muted-foreground mb-3 truncate">« {modal.doc.title} »</p>
              <div className="flex flex-col gap-1 max-h-[280px] overflow-y-auto -mx-1 px-1">
                {[{ id: UNFILED, name: 'Non classés' }, ...folders].map((f) => {
                  const selected = draftFolder === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setDraftFolder(f.id)}
                      aria-pressed={selected}
                      className={cn(
                        'flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-left text-[14px] font-medium border transition-colors cursor-pointer font-[inherit]',
                        selected
                          ? 'bg-accent text-primary border-primary'
                          : 'bg-transparent text-foreground border-transparent hover:bg-muted',
                      )}
                    >
                      {f.id === UNFILED ? <FolderDashed size={17} /> : <Folder size={17} weight="fill" />}
                      <span className="truncate">{f.name}</span>
                    </button>
                  );
                })}
              </div>
            </ModalShell>
          )}

          {modal.kind === 'bulk-move' && (
            <ModalShell
              title="Déplacer les documents"
              confirmLabel={modalBusy ? 'Déplacement…' : 'Déplacer'}
              confirmDisabled={modalBusy}
              error={modalError}
              onConfirm={() => void commitBulkMove(modal.ids)}
              onCancel={closeModal}
            >
              <p className="text-[13px] text-muted-foreground mb-3">
                {plural(modal.ids.length, 'document', 'documents')} sélectionné{modal.ids.length > 1 ? 's' : ''}
              </p>
              <div className="flex flex-col gap-1 max-h-[280px] overflow-y-auto -mx-1 px-1">
                {[{ id: UNFILED, name: 'Non classés' }, ...folders].map((f) => {
                  const selected = draftFolder === f.id;
                  return (
                    <button
                      key={f.id}
                      type="button"
                      onClick={() => setDraftFolder(f.id)}
                      aria-pressed={selected}
                      className={cn(
                        'flex items-center gap-2.5 min-h-[44px] px-3 rounded-md text-left text-[14px] font-medium border transition-colors cursor-pointer font-[inherit]',
                        selected
                          ? 'bg-accent text-primary border-primary'
                          : 'bg-transparent text-foreground border-transparent hover:bg-muted',
                      )}
                    >
                      {f.id === UNFILED ? <FolderDashed size={17} /> : <Folder size={17} weight="fill" />}
                      <span className="truncate">{f.name}</span>
                    </button>
                  );
                })}
              </div>
            </ModalShell>
          )}

          {modal.kind === 'bulk-archive' && (
            <ModalShell
              title="Archiver ces documents ?"
              confirmLabel={modalBusy ? 'Archivage…' : 'Archiver'}
              confirmDisabled={modalBusy}
              destructive
              error={modalError}
              onConfirm={() => void commitBulkArchive(modal.ids)}
              onCancel={closeModal}
            >
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                {plural(modal.ids.length, 'document', 'documents')} sélectionné{modal.ids.length > 1 ? 's' : ''} seront
                déplacés dans la corbeille. Vous pourrez les restaurer à tout moment.
              </p>
            </ModalShell>
          )}

          {modal.kind === 'doc-archive' && (
            <ModalShell
              title="Archiver ce document ?"
              confirmLabel={modalBusy ? 'Archivage…' : 'Archiver'}
              confirmDisabled={modalBusy}
              destructive
              error={modalError}
              onConfirm={() => void commitDocArchive(modal.doc)}
              onCancel={closeModal}
            >
              <p className="text-[14px] text-muted-foreground leading-relaxed">
                « {modal.doc.title} » sera déplacé dans la corbeille. Vous pourrez le restaurer à tout moment.
              </p>
            </ModalShell>
          )}
        </DialogContent>
      </Dialog>

      {sendDoc && (
        <SendAssignmentModal
          documentId={sendDoc.id}
          docTitle={sendDoc.title}
          onClose={() => setSendDoc(null)}
          onSent={(count) => {
            setSendDoc(null);
            setSentToast(`Devoir envoyé à ${count} élève${count > 1 ? 's' : ''}.`);
          }}
        />
      )}
    </div>
  );
}

function ModalShell({
  title, children, confirmLabel, onConfirm, onCancel, destructive = false, confirmDisabled = false, error,
}: {
  title: string;
  children: React.ReactNode;
  confirmLabel: string;
  onConfirm: () => void;
  onCancel: () => void;
  destructive?: boolean;
  confirmDisabled?: boolean;
  error?: string | null;
}) {
  return (
    <>
      <DialogHeader>
        <DialogTitle>{title}</DialogTitle>
      </DialogHeader>
      <div className="px-5 py-4 overflow-y-auto flex flex-col gap-3">
        {error && <Alert>{error}</Alert>}
        {children}
      </div>
      <div className="flex justify-end gap-2 px-5 pb-5 pt-1">
        <Button variant="ghost" size="lg" className="min-h-[44px]" onClick={onCancel}>Annuler</Button>
        <Button
          size="lg"
          variant={destructive ? 'destructive' : 'default'}
          className="min-h-[44px]"
          disabled={confirmDisabled}
          onClick={onConfirm}
        >
          {confirmLabel}
        </Button>
      </div>
    </>
  );
}

function FolderCard({
  name, count, hint, icon, index, dashed = false, onOpen, onRename, onDelete,
}: {
  name: string;
  count?: number;
  hint?: string;
  icon: React.ReactNode;
  index?: number;
  dashed?: boolean;
  onOpen: () => void;
  onRename?: () => void;
  onDelete?: () => void;
}) {
  return (
    <div
      className={cn(
        'group relative flex items-center gap-3 rounded-card border bg-card px-3 py-2.5 transition-shadow',
        'hover:shadow-card focus-within:shadow-card',
        dashed ? 'border-dashed border-border bg-muted/40' : 'border-border',
      )}
    >
      <button
        type="button"
        onClick={onOpen}
        aria-label={hint ? name : `Ouvrir le dossier ${name}`}
        className="absolute inset-0 rounded-card bg-transparent border-none cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      />

      <div className="relative flex items-center gap-3 flex-1 min-w-0 pointer-events-none">
        <span className={cn('relative shrink-0 grid place-items-center', dashed ? 'text-muted-foreground' : 'text-primary')}>
          {icon}
          {index !== undefined && (
            <span className="absolute inset-x-0 top-[52%] -translate-y-1/2 text-center font-mono text-[11px] font-bold leading-none text-primary-foreground pointer-events-none">{index}</span>
          )}
        </span>
        <span className="flex-1 min-w-0">
          <span className="block text-[13.5px] font-semibold text-foreground leading-[1.25] line-clamp-2">
            {name}
          </span>
          <span className="block text-[11.5px] text-muted-foreground mt-0.5">
            {hint ?? (count === 0 ? 'Vide' : plural(count ?? 0, 'document', 'documents'))}
          </span>
        </span>
      </div>

      {(onRename || onDelete) && (
        <DropdownMenu>
          <RowMenuTrigger label={`Options du dossier ${name}`} />
          <DropdownMenuContent align="end">
            {onRename && (
              <DropdownMenuItem onClick={onRename}>
                <PencilSimpleLine size={15} />
                Renommer
              </DropdownMenuItem>
            )}
            {onRename && onDelete && <DropdownMenuSeparator />}
            {onDelete && (
              <DropdownMenuItem variant="destructive" onClick={onDelete}>
                <Trash size={15} />
                Supprimer
              </DropdownMenuItem>
            )}
          </DropdownMenuContent>
        </DropdownMenu>
      )}
    </div>
  );
}

function ClassSection({
  label, summary, open, onToggle, children,
}: {
  label: string;
  summary: string;
  open: boolean;
  onToggle: () => void;
  children: React.ReactNode;
}) {
  const bodyId = useId();

  return (
    <section
      aria-label={label}
      className="rounded-card border border-[var(--naia-line-strong)] bg-[var(--naia-card-alt)]"
    >
      <h2 className="m-0 sticky top-[var(--naia-app-header-h,0px)] z-20">
        <button
          type="button"
          onClick={onToggle}
          aria-expanded={open}
          aria-controls={bodyId}
          className={cn(
            'w-full flex items-center gap-2.5 min-h-[52px] px-3 py-2 text-left',
            'bg-[var(--naia-sky)] border-0 cursor-pointer font-[inherit] rounded-t-[15px]',
            'focus-visible:outline-2 focus-visible:outline-offset-[-2px] focus-visible:outline-primary',
            open ? 'border-b border-[var(--naia-line-strong)]' : 'rounded-b-[15px]',
          )}
        >
          <CaretDown
            size={14}
            weight="bold"
            className={cn('shrink-0 text-primary transition-transform', !open && '-rotate-90')}
          />
          <span className="flex-1 min-w-0 truncate text-[15px] font-extrabold text-foreground leading-tight">
            {label}
          </span>
          <span className="shrink-0 text-[11.5px] font-bold text-muted-foreground">{summary}</span>
        </button>
      </h2>

      <div id={bodyId} hidden={!open} className="p-3">
        {children}
      </div>
    </section>
  );
}

function TrashCard({ onOpen }: { onOpen: () => void }) {
  return (
    <button
      type="button"
      onClick={onOpen}
      className="group relative flex items-center gap-3 rounded-card border border-dashed border-border bg-transparent px-3 py-2.5 text-left cursor-pointer font-[inherit] transition-colors hover:bg-muted/50"
    >
      <span className="relative shrink-0 grid place-items-center text-destructive">
        <Trash size={34} weight="regular" />
      </span>
      <span className="flex-1 min-w-0">
        <span className="block text-[13.5px] font-semibold text-foreground leading-[1.25]">Corbeille</span>
        <span className="block text-[11.5px] text-muted-foreground mt-0.5">Documents archivés</span>
      </span>
    </button>
  );
}

function SelectionBar({
  count, allSelected, onToggleSelectAll, onMove, onArchive, onClear,
}: {
  count: number;
  allSelected: boolean;
  onToggleSelectAll: () => void;
  onMove: () => void;
  onArchive: () => void;
  onClear: () => void;
}) {
  return (
    <div className="sticky top-[var(--naia-app-header-h,0px)] z-30 flex items-center gap-3.5 mb-1.5 border-b border-border px-1 py-2 bg-background text-[13px]">
      <span className="font-semibold text-foreground flex-1 min-w-0">
        {plural(count, 'document sélectionné', 'documents sélectionnés')}
      </span>
      <button
        type="button"
        onClick={onToggleSelectAll}
        className="flex items-center gap-1.5 min-h-[44px] px-1 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] text-muted-foreground hover:text-primary"
      >
        <CheckSquare size={15} weight="bold" />
        {allSelected ? 'Tout désélectionner' : 'Tout sélectionner'}
      </button>
      <span aria-hidden="true" className="w-px h-[18px] bg-border" />
      <button
        type="button"
        onClick={onMove}
        className="flex items-center gap-1.5 min-h-[44px] px-1 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] text-muted-foreground hover:text-primary"
      >
        <FolderSimplePlus size={15} weight="bold" />
        Déplacer
      </button>
      <span aria-hidden="true" className="w-px h-[18px] bg-border" />
      <button
        type="button"
        onClick={onArchive}
        className="flex items-center gap-1.5 min-h-[44px] px-1 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] text-destructive hover:text-destructive/80"
      >
        <Archive size={15} weight="bold" />
        Archiver
      </button>
      <span aria-hidden="true" className="w-px h-[18px] bg-border" />
      <button
        type="button"
        onClick={onClear}
        className="flex items-center gap-1.5 min-h-[44px] px-1 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] text-muted-foreground hover:text-primary"
      >
        <X size={15} weight="bold" />
        Annuler
      </button>
    </div>
  );
}

function SelectAllEntry({ onSelectAll }: { onSelectAll: () => void }) {
  return (
    <button
      type="button"
      onClick={onSelectAll}
      className="flex items-center gap-1.5 self-start min-h-11 px-1 mb-0.5 bg-transparent border-none cursor-pointer font-[inherit] text-[12.5px] text-muted-foreground hover:text-primary"
    >
      <CheckSquare size={15} weight="bold" />
      Tout sélectionner
    </button>
  );
}

function DocRowItem({
  doc, folderLabel, levelLabel, subjectLabel, onOpen, onSend, onMove, onArchive,
  selected, onToggleSelect,
}: {
  doc: DocRow;
  folderLabel?: string;
  levelLabel: string;
  subjectLabel: string;
  onOpen: () => void;
  onSend: () => void;
  onMove: () => void;
  onArchive: () => void;
  selected?: boolean;
  onToggleSelect?: () => void;
}) {
  const meta = KIND[doc.kind];
  const Icon = meta.icon;
  return (
    <div className="group relative flex items-center gap-3 rounded-card border border-border bg-card pl-3 pr-1 py-2 transition-shadow hover:shadow-card focus-within:shadow-card">
      <button
        type="button"
        onClick={onOpen}
        aria-label={`Ouvrir ${doc.title}`}
        className="absolute inset-0 rounded-card bg-transparent border-none cursor-pointer focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
      />

      {onToggleSelect && (
        <button
          type="button"
          onClick={onToggleSelect}
          aria-pressed={selected}
          aria-label={selected ? `Désélectionner ${doc.title}` : `Sélectionner ${doc.title}`}
          className="group/checkbox relative shrink-0 grid place-items-center min-w-[44px] min-h-[44px] -ml-3 bg-transparent border-none cursor-pointer font-[inherit]"
        >
          <span
            className={cn(
              'grid place-items-center w-[18px] h-[18px] rounded-md border-2',
              selected ? 'border-primary bg-primary text-primary-foreground' : 'border-border text-transparent group-hover/checkbox:border-primary',
            )}
          >
            <Check size={11} weight="bold" />
          </span>
        </button>
      )}

      <span className={cn('relative w-9 h-9 rounded-md bg-muted grid place-items-center shrink-0 pointer-events-none', meta.tone)}>
        <Icon size={18} weight="regular" />
      </span>

      <span className="relative flex-1 min-w-0 pointer-events-none">
        <span className="block text-[14px] font-semibold text-foreground truncate">{doc.title}</span>
        <span className="block text-[12px] text-muted-foreground truncate mt-0.5">
          {meta.label} · {levelLabel} · {subjectLabel} · modifié le {fmtDate(doc.updated_at)}
        </span>
        {folderLabel && (
          <span className="inline-flex items-center gap-1 mt-1 text-[11px] text-muted-foreground">
            <Folder size={11} weight="fill" />
            {folderLabel}
          </span>
        )}
      </span>

      <DropdownMenu>
        <RowMenuTrigger label={`Options de ${doc.title}`} />
        <DropdownMenuContent align="end">
          <DropdownMenuItem onClick={onOpen}>
            <PencilSimple size={15} />
            Ouvrir dans l’éditeur
          </DropdownMenuItem>
          {doc.kind === 'exercice_sheet' && (
            <DropdownMenuItem onClick={onSend}>
              <PaperPlaneTilt size={15} />
              Envoyer en devoir
            </DropdownMenuItem>
          )}
          <DropdownMenuItem onClick={onMove}>
            <FolderSimplePlus size={15} />
            Déplacer vers…
          </DropdownMenuItem>
          <DropdownMenuSeparator />
          <DropdownMenuItem variant="destructive" onClick={onArchive}>
            <Archive size={15} />
            Archiver
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    </div>
  );
}

function TrashRow({
  doc, levelLabel, subjectLabel, onRestore,
}: {
  doc: DocRow;
  levelLabel: string;
  subjectLabel: string;
  onRestore: () => void;
}) {
  const meta = KIND[doc.kind];
  const Icon = meta.icon;
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-card border border-border bg-card px-3 py-2.5">
      <span className={cn('w-9 h-9 rounded-md bg-muted grid place-items-center shrink-0', meta.tone)}>
        <Icon size={18} weight="regular" />
      </span>

      <span className="flex-1 min-w-[140px]">
        <span className="block text-[14px] font-semibold text-foreground truncate">{doc.title}</span>
        <span className="block text-[12px] text-muted-foreground truncate mt-0.5">
          {meta.label} · {levelLabel} · {subjectLabel}
          {doc.archived_at && ` · archivé le ${fmtDate(doc.archived_at)}`}
        </span>
      </span>

      <span className="flex items-center gap-2 shrink-0 ml-auto">
        <Button variant="outline" size="lg" className="min-h-[44px]" onClick={onRestore}>
          <ArrowCounterClockwise size={15} weight="bold" />
          Restaurer
        </Button>
      </span>
    </div>
  );
}
